import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import LoginPage from './pages/LoginPage';
import UserMenu from './components/UserMenu';
import UpdateProfileModal from './components/UpdateProfileModal';
import Maintenance from './components/Maintenance';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ComparisonProvider, useComparison } from './contexts/ComparisonContext';
import { ModalProvider } from './contexts/ModalContext';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ScrollRestoration from './components/ScrollRestoration';
import { useCompanySettings } from './hooks/useCompanySettings';
import { useFavicon } from './hooks/useFavicon';
import { usePrimaryColor } from './hooks/usePrimaryColor';
import { useLogo } from './hooks/useLogo';
import useSystemStatus from './hooks/useSystemStatus';
import ProtectedRoute from './components/ProtectedRoute';
import PropertyFormSkeleton from './components/PropertyFormSkeleton';
import ComparisonSkeleton from './components/ComparisonSkeleton';
import PropertyDetailsSkeleton from './components/PropertyDetailsSkeleton';
import { ListingPageSkeleton } from './components/PropertyGridSkeleton';
import { BlogListingSkeleton, BlogPostSkeleton } from './components/BlogSkeletons';
import PreLoader from './components/PreLoader';
import Comparison from './pages/Comparison';
import Favorites from './pages/Favorites';
import Home from './pages/Home';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Contact from './pages/Contact';
import About from './pages/About';
import CondominiumsPublicList from './pages/CondominiumsPublicList';
import CondominiumDetails from './pages/CondominiumDetails';
import PropertyList from './components/PropertyList';
import PropertyRouteResolver from './components/PropertyRouteResolver';
import { usePermissions } from './hooks/usePermissions';
import { useHasPublicCondominiums } from './hooks/useHasPublicCondominiums';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';
import CookieConsentBanner from './components/CookieConsentBanner';
import { CookieConsentProvider } from './contexts/CookieConsentContext';

const PropertyForm = lazy(() => import('./components/PropertyForm'));
const DuplicateProperty = lazy(() => import('./pages/DuplicateProperty'));
const DuplicateCondominium = lazy(() => import('./pages/DuplicateCondominium'));
const CondominiumPlaceholderPage = lazy(() => import('./pages/CondominiumPlaceholderPage'));
const Admin = lazy(() => import('./pages/Admin'));
const Master = lazy(() => import('./pages/Master'));
const MercadoLivreOAuthCallback = lazy(() => import('./pages/MercadoLivreOAuthCallback'));
const GoogleCalendarOAuthCallback = lazy(() => import('./pages/GoogleCalendarOAuthCallback'));
const SharedList = lazy(() => import('./pages/SharedList'));
const LandingPreviewPage = lazy(() => import('./pages/LandingPreviewPage'));
const LandingPublicPage = lazy(() => import('./pages/LandingPublicPage'));
const LinkBioPublicLayout = lazy(() => import('./pages/LinkBioPublicLayout'));
const LinkBioPublicPage = lazy(() => import('./pages/LinkBioPublicPage'));
const LinkBioPropertiesPublicPage = lazy(() => import('./pages/LinkBioPropertiesPublicPage'));
const ChatButton = lazy(() => import('./components/ChatButton'));
const AdminAIFloatingButton = lazy(() => import('./components/AdminAIFloatingButton'));
const TaskNotificationService = lazy(() =>
  import('./components/TaskNotificationService').then((m) => ({ default: m.TaskNotificationService }))
);
const NotFound = lazy(() => import('./pages/NotFound'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'));

/** Cobre navbar/footer enquanto um chunk lazy carrega (evita flash feio). */
function RouteFallback() {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-white"
      aria-busy="true"
      aria-label="Carregando"
    >
      <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
    </div>
  );
}

function isPropertyFormPathname(pathname = '') {
  return (
    pathname === '/cadastrar' ||
    pathname.startsWith('/editar/') ||
    pathname.startsWith('/duplicar/') ||
    pathname === '/cadastrar-condominio' ||
    pathname.startsWith('/editar-condominio/') ||
    pathname.startsWith('/duplicar-condominio/')
  );
}

function propertyFormDocumentTitle(pathname = '') {
  if (pathname.startsWith('/editar-condominio')) return 'Editar condomínio';
  if (pathname.startsWith('/duplicar-condominio')) return 'Duplicar condomínio';
  if (pathname === '/cadastrar-condominio') return 'Cadastrar condomínio';
  if (pathname.startsWith('/editar/')) return 'Editar Imóvel';
  if (pathname.startsWith('/duplicar/')) return 'Duplicar Imóvel';
  if (pathname === '/cadastrar') return 'Cadastrar Imóvel';
  return 'Imóvel';
}

function isPublicPreloaderPath(pathname = '') {
  if (
    pathname === '/' ||
    pathname === '/contato' ||
    pathname === '/sobre' ||
    pathname === '/blog' ||
    pathname === '/favoritos' ||
    pathname === '/comparacao'
  ) {
    return true;
  }
  if (pathname.startsWith('/blog/')) return true;
  if (pathname.startsWith('/propriedades')) return true;
  if (pathname.startsWith('/condominios')) return true;
  return false;
}

function getPublicRouteSkeleton(pathname = '') {
  if (pathname === '/') {
    // Home: só PreLoader (sem skeleton)
    return null;
  }
  if (pathname === '/comparacao') return <ComparisonSkeleton />;
  if (/^\/condominios\/[^/]+/.test(pathname)) return <PropertyDetailsSkeleton />;
  if (/^\/propriedades\/.+/.test(pathname) && !pathname.startsWith('/propriedades/tipo/')) {
    // Pode ser detalhe (slug-id) ou listagem por cidade — skeleton de detalhe cobre o wait do resolver
    const last = pathname.split('/').pop() || '';
    if (last.includes('-') || last.length > 20) return <PropertyDetailsSkeleton />;
  }
  if (pathname.startsWith('/blog/')) {
    return <BlogPostSkeleton />;
  }
  if (pathname === '/blog') {
    return (
      <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        <div className="mx-auto max-w-7xl px-4 py-12">
          <BlogListingSkeleton count={8} />
        </div>
      </div>
    );
  }
  return <ListingPageSkeleton />;
}

function AppRouteFallback() {
  const location = useLocation();
  if (isPropertyFormPathname(location.pathname)) {
    const title = propertyFormDocumentTitle(location.pathname);
    return (
      <div className="min-h-screen bg-white pt-2">
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <PropertyFormSkeleton />
      </div>
    );
  }
  if (location.pathname === '/') {
    return <PreLoader forceShow isLoading onLoadingComplete={() => {}} />;
  }
  if (isPublicPreloaderPath(location.pathname)) {
    return (
      <>
        {/* PreLoader só no F5/primeiro acesso; na navegação interna ele pula e o skeleton fica visível */}
        <PreLoader isLoading onLoadingComplete={() => {}} />
        {getPublicRouteSkeleton(location.pathname)}
      </>
    );
  }
  return <RouteFallback />;
}

function PropertyFormRouteLoadingFallback() {
  const location = useLocation();
  const title = propertyFormDocumentTitle(location.pathname);
  return (
    <div className="min-h-screen bg-white pt-2">
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <PropertyFormSkeleton />
    </div>
  );
}

/** Rota pública LinkBio: exige slug (/linkBio/meu-slug ou /linkbio/meu-slug/imoveis). */
function isLinkBioPublicPathname(pathname) {
  return /^\/linkbio\/[^/]+/i.test(pathname);
}

function NavBar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUpdateProfileOpen, setIsUpdateProfileOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { companyName } = useCompanySettings();
  const { logo } = useLogo('navbar');
  const { getComparisonCount } = useComparison();
  const [hasLocalData, setHasLocalData] = useState(false);
  const { canAccessAdmin, canCreateProperties, userProfile } = usePermissions();
  const { isMasterUser } = useSystemStatus();
  const hasCondominiums = useHasPublicCondominiums();

  // Verifica se há dados locais (favoritos ou comparação)
  useEffect(() => {
    const checkLocalData = () => {
      const localFavorites = JSON.parse(localStorage.getItem('localFavorites') || '[]');
      const comparisonCount = getComparisonCount();
      setHasLocalData(localFavorites.length > 0 || comparisonCount > 0);
    };

    checkLocalData();

    // Listener para mudanças no localStorage
    window.addEventListener('storage', checkLocalData);
    
    // Listener para mudanças no localStorage na mesma aba
    const interval = setInterval(checkLocalData, 1000);

    return () => {
      window.removeEventListener('storage', checkLocalData);
      clearInterval(interval);
    };
  }, [getComparisonCount]);

  const skipMenuScrollRestoreRef = React.useRef(false);

  // Ao mudar de rota, libera o lock do menu sem restaurar a posição antiga
  React.useEffect(() => {
    skipMenuScrollRestoreRef.current = true;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Previne scroll quando menu hambúrguer está aberto
  React.useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';

      if (skipMenuScrollRestoreRef.current) {
        skipMenuScrollRestoreRef.current = false;
      } else if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }

    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const isActive = (path) => {
    return location.pathname === path ? 'text-blue-600' : 'text-blue-500';
  };

  return (
    <nav
      aria-label="Principal"
      className={`bg-white shadow-md ${isHome ? 'fixed' : 'relative'} top-0 left-0 right-0 z-50 font-montserrat`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center gap-3">
              {logo && (
                <img src={logo} alt={companyName || 'Logo'} className="h-16 w-auto" />
              )}
              {companyName && (
                <span className="text-lg font-semibold text-gray-900 font-montserrat">{companyName}</span>
              )}
            </Link>
          </div>
          <ul className="hidden lg:flex items-center space-x-8 list-none m-0 p-0">
            <li>
              <Link
                to="/"
                className={`${isActive('/')} hover:text-blue-600 px-3 py-2 rounded-md text-md font-semibold transition-all duration-300 font-montserrat relative group`}
              >
                Início
                <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            </li>
            <li>
              <Link
                to="/propriedades"
                className={`${isActive('/propriedades')} hover:text-blue-600 px-3 py-2 rounded-md text-md font-semibold transition-all duration-300 font-montserrat relative group`}
              >
                Propriedades
                <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/propriedades' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            </li>
            {hasCondominiums && (
              <li>
                <Link
                  to="/condominios"
                  className={`${isActive('/condominios')} hover:text-blue-600 px-3 py-2 rounded-md text-md font-semibold transition-all duration-300 font-montserrat relative group`}
                >
                  Condomínios
                  <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/condominios' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                </Link>
              </li>
            )}
            <li>
              <Link
                to="/contato"
                className={`${isActive('/contato')} hover:text-blue-600 px-3 py-2 rounded-md text-md font-semibold transition-all duration-300 font-montserrat relative group`}
              >
                Contato
                <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/contato' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            </li>
            <li>
              <Link
                to="/sobre"
                className={`${isActive('/sobre')} hover:text-blue-600 px-3 py-2 rounded-md text-md font-semibold transition-all duration-300 font-montserrat relative group`}
              >
                Sobre nós
                <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/sobre' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            </li>
            <li>
              <Link
                to="/blog"
                className={`${isActive('/blog')} hover:text-blue-600 px-3 py-2 rounded-md text-md font-semibold transition-all duration-300 font-montserrat relative group`}
              >
                Blog
                <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/blog' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            </li>
            <li className="list-none">
              {user || hasLocalData ? (
                <UserMenu />
              ) : (
                <button
                  onClick={() => navigate('/login', { state: { from: location } })}
                  className="bg-primary text-white hover:bg-primary-hover px-4 py-2 rounded-md text-sm font-semibold transition-all duration-300 font-montserrat"
                >
                  Login
                </button>
              )}
            </li>
          </ul>

          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-500 hover:bg-gray-100 focus:outline-none"
            >
              <svg
                className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <svg
                className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div 
          className={`
            lg:hidden 
            bg-white 
            fixed
            top-20
            left-0
            right-0
            min-h-screen
            backdrop-blur-sm
            bg-white/95
            transform 
            transition-all 
            duration-300 
            ease-in-out
            z-50
            ${isMobileMenuOpen 
              ? 'opacity-100 translate-x-0' 
              : 'opacity-0 translate-x-full pointer-events-none'
            }
          `}
        >
          <ul className="px-6 py-2 flex flex-col gap-2 list-none m-0">
            <li>
              <Link
                to="/"
                className={`block px-3 py-2 rounded-md text-base font-semibold font-montserrat relative group transition-all duration-300 ${
                  isActive('/') ? 'text-primary' : 'text-gray-700 hover:text-primary'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Início
                <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            </li>
            <li>
              <Link
                to="/propriedades"
                className={`block px-3 py-2 rounded-md text-base font-semibold font-montserrat relative group transition-all duration-300 ${
                  isActive('/propriedades') ? 'text-primary' : 'text-gray-700 hover:text-primary'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Propriedades
                <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/propriedades' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            </li>
            {hasCondominiums && (
              <li>
                <Link
                  to="/condominios"
                  className={`block px-3 py-2 rounded-md text-base font-semibold font-montserrat relative group transition-all duration-300 ${
                    isActive('/condominios') ? 'text-primary' : 'text-gray-700 hover:text-primary'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Condomínios
                  <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/condominios' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                </Link>
              </li>
            )}
            <li>
              <Link
                to="/contato"
                className={`block px-3 py-2 rounded-md text-base font-semibold font-montserrat relative group transition-all duration-300 ${
                  isActive('/contato') ? 'text-primary' : 'text-gray-700 hover:text-primary'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contato
                <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/contato' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            </li>
            <li>
              <Link
                to="/sobre"
                className={`block px-3 py-2 rounded-md text-base font-semibold font-montserrat relative group transition-all duration-300 ${
                  isActive('/sobre') ? 'text-primary' : 'text-gray-700 hover:text-primary'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Sobre nós
                <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/sobre' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            </li>
            <li>
              <Link
                to="/blog"
                className={`block px-3 py-2 rounded-md text-base font-semibold font-montserrat relative group transition-all duration-300 ${
                  isActive('/blog') ? 'text-primary' : 'text-gray-700 hover:text-primary'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Blog
                <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/blog' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            </li>
            {user || hasLocalData ? (
              user && user.isAdmin ? (
                <li className="space-y-2">
                  {/* Informações do usuário admin */}
                  <div className="flex items-center justify-center px-3 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-600">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900 font-montserrat">Administrador</p>
                        <p className="text-xs text-gray-500 font-montserrat">{user?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Opções do usuário admin */}
                  <div className="space-y-1">
                    {/* Área do Master - Para usuário master */}
                    {user && isMasterUser(user) && (
                      <button
                        onClick={() => {
                          navigate('/master');
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                        </svg>
                        Área do Master
                        <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/master' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                      </button>
                    )}

                    <button
                      onClick={() => {
                        navigate('/admin');
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Área do Admin
                      <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/admin' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                    </button>

                    <button
                      onClick={() => {
                        navigate('/cadastrar');
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Adicionar Propriedade
                      <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/cadastrar' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                    </button>

                    <button
                      onClick={() => {
                        navigate('/favoritos');
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                      </svg>
                      Favoritos
                      <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/favoritos' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                    </button>

                    <button
                      onClick={() => {
                        navigate('/comparacao');
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                      </svg>
                      Comparação
                      <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/comparacao' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                    </button>

                    <button
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-red-600 hover:bg-red-50 transition-all duration-300 rounded-lg font-montserrat relative group"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-red-500">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                      </svg>
                      Sair
                      <span className="absolute bottom-0 left-0 w-0 h-[3px] bg-red-600 transition-all duration-300 group-hover:w-full"></span>
                    </button>
                  </div>
                </li>
              ) : (
                <li className="space-y-2">
                  {/* Informações do usuário */}
                  <div className="flex items-center justify-center px-3 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-gray-600">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                        </svg>
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900 font-montserrat">
                          {user ? (user?.isAdmin ? 'Administrador' : (userProfile?.name || user?.name || 'Usuário')) : 'Visitante'}
                        </p>
                        <p className="text-xs text-gray-500 font-montserrat">
                          {user ? user?.email : 'Não conectado'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Opções do usuário */}
                  <div className="space-y-1">
                    {/* Área do Master - Para usuário master */}
                    {user && isMasterUser(user) && (
                      <button
                        onClick={() => {
                          navigate('/master');
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                        </svg>
                        Área do Master
                        <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/master' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                      </button>
                    )}

                    {/* Área Admin / Área do Corretor */}
                    {user && canAccessAdmin() && (
                      <button
                        onClick={() => {
                          navigate('/admin');
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {user?.isAdmin ? 'Área Admin' : 'Área do Corretor'}
                        <span className={`absolute bottom-0 left-0 w-0 h-[3px] bg-primary transition-all duration-300 group-hover:w-full`}></span>
                      </button>
                    )}

                    {/* Adicionar Propriedade */}
                    {user && canCreateProperties() && (
                      <button
                        onClick={() => {
                          navigate('/cadastrar');
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        Adicionar Propriedade
                        <span className={`absolute bottom-0 left-0 w-0 h-[3px] bg-primary transition-all duration-300 group-hover:w-full`}></span>
                      </button>
                    )}

                    <button
                      onClick={() => {
                        navigate('/favoritos');
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                      </svg>
                      Favoritos
                      <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/favoritos' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                    </button>

                    <button
                      onClick={() => {
                        navigate('/comparacao');
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                      </svg>
                      Comparação
                      <span className={`absolute bottom-0 left-1/2 h-[3px] bg-primary transition-all duration-300 transform -translate-x-1/2 ${location.pathname === '/comparacao' ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                    </button>

                    {/* Atualizar Perfil - Apenas para usuários sem acesso admin */}
                    {user && !canAccessAdmin() && (
                      <button
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setIsUpdateProfileOpen(true);
                        }}
                        className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-gray-700 hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-gray-400">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                        </svg>
                        Atualizar Perfil
                        <span className="absolute bottom-0 left-0 w-0 h-[3px] bg-primary transition-all duration-300 group-hover:w-full"></span>
                      </button>
                    )}

                    {user ? (
                      <button
                        onClick={() => {
                          logout();
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-red-600 hover:bg-red-50 transition-all duration-300 rounded-lg font-montserrat relative group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3 text-red-500">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                        </svg>
                        Sair
                        <span className="absolute bottom-0 left-0 w-0 h-[3px] bg-red-600 transition-all duration-300 group-hover:w-full"></span>
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          navigate('/login', { state: { from: location } });
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center w-full px-4 py-3 text-left text-base font-semibold text-primary hover:bg-gray-100 transition-all duration-300 rounded-lg font-montserrat relative group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                        </svg>
                        Fazer Login
                      </button>
                    )}
                  </div>
                </li>
              )
            ) : (
              <li>
                <button
                  onClick={() => {
                    navigate('/login', { state: { from: location } });
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-center px-3 py-2 rounded-full text-base font-semibold bg-primary text-white hover:bg-primary-hover font-montserrat relative group transition-all duration-300"
                >
                  Login
                  <span className="absolute bottom-0 left-0 w-0 h-[3px] bg-white transition-all duration-300 group-hover:w-full"></span>
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>
      <UpdateProfileModal isOpen={isUpdateProfileOpen} onClose={() => setIsUpdateProfileOpen(false)} />
    </nav>
  );
}

function MainLayout({ children }) {
  useFavicon();
  usePrimaryColor();
  
  const location = useLocation();
  const isAdmin = location.pathname === '/admin';
  const isMaster = location.pathname === '/master';
  const isLoginPage = location.pathname === '/login';
  const isLinkBioPublic = isLinkBioPublicPathname(location.pathname);
  const isPropertyForm = isPropertyFormPathname(location.pathname);
  const showPublicChrome = !isAdmin && !isMaster && !isLoginPage && !isLinkBioPublic;
  const showFooter = showPublicChrome && !isPropertyForm;

  /**
   * O tema escuro é uma escolha exclusiva do painel administrativo. Como o app é uma SPA,
   * a classe `dark` aplicada no <html> pelo Admin persiste ao navegar para o front.
   * Aqui garantimos que qualquer rota fora de /admin e /master seja SEMPRE clara — o tema
   * escolhido no admin jamais pode interferir nas telas públicas.
   */
  const pathname = location.pathname;
  useEffect(() => {
    const isThemedRoute =
      pathname === '/admin' ||
      pathname.startsWith('/admin/') ||
      pathname === '/master' ||
      pathname.startsWith('/master/');
    if (!isThemedRoute) {
      const root = window.document.documentElement;
      root.classList.remove('dark');
      root.classList.add('light');
      root.style.colorScheme = 'light';
    }
  }, [pathname]);

  return (
    <div className={`min-h-screen bg-white`}>
      {showPublicChrome && <NavBar />}
      {children}
      {showFooter && <Footer />}
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const { user } = useAuth();
  const { shouldShowMaintenance, isLoading } = useSystemStatus();
  const isLinkBioPublicPath = isLinkBioPublicPathname(location.pathname);
  // O FAB do Assistente IA aparece em todas as rotas (front + área admin), exceto:
  // - /admin: o próprio Admin renderiza o botão (precisa da seção ativa para se ocultar no Assistente)
  // - /master, /login e bio pública: telas sem contexto administrativo
  const showAdminAiFabOutsideAdmin =
    location.pathname !== '/admin' &&
    location.pathname !== '/master' &&
    location.pathname !== '/login' &&
    !isLinkBioPublicPath;

  // Não bloqueia com spinner global — cada página pública usa o PreLoader personalizado.
  // Aguarda o status do sistema apenas para decidir manutenção (sem tela de loading intermediária).
  if (!isLoading && shouldShowMaintenance(user)) {
    return <Maintenance />;
  }
  
  return (
    <>
      <ScrollRestoration />
      <AnalyticsRouteTracker />
      <MainLayout>
        <Suspense fallback={<AppRouteFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<Home />} />
            <Route path="/propriedades" element={<PropertyList />} />
            {/* Listagens amigáveis: /propriedades/{cidade}[/{bairro|tipo}][/{tipo}] */}
            <Route path="/propriedades/tipo/:typeSlug" element={<PropertyList />} />
            <Route path="/propriedades/:seg1/:seg2/:seg3" element={<PropertyList />} />
            <Route path="/propriedades/:seg1/:seg2" element={<PropertyList />} />
            {/* Ambíguo: cidade (listagem) ou slug-id / id (detalhe) — resolvido em runtime */}
            <Route path="/propriedades/:slugAndId" element={<PropertyRouteResolver />} />
            <Route path="/condominios" element={<CondominiumsPublicList />} />
            <Route path="/condominios/:id" element={<CondominiumDetails />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute permission="accessAdmin">
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/integracoes/ml/callback"
              element={
                <ProtectedRoute permission="accessAdmin">
                  <MercadoLivreOAuthCallback />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/integracoes/google-calendar/callback"
              element={
                <ProtectedRoute permission="accessAdmin">
                  <GoogleCalendarOAuthCallback />
                </ProtectedRoute>
              }
            />
            <Route path="/master" element={<Master />} />
            <Route
              path="/cadastrar"
              element={
                <ProtectedRoute
                  permission="createProperties"
                  loadingFallback={<PropertyFormRouteLoadingFallback />}
                >
                  <PropertyForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editar/:id"
              element={
                <ProtectedRoute
                  permission="editProperties"
                  loadingFallback={<PropertyFormRouteLoadingFallback />}
                >
                  <PropertyForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/duplicar/:id"
              element={
                <ProtectedRoute
                  permission="createProperties"
                  loadingFallback={<PropertyFormRouteLoadingFallback />}
                >
                  <DuplicateProperty />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cadastrar-condominio"
              element={
                <ProtectedRoute
                  permission="createProperties"
                  loadingFallback={<PropertyFormRouteLoadingFallback />}
                >
                  <CondominiumPlaceholderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editar-condominio/:id"
              element={
                <ProtectedRoute
                  permission="editProperties"
                  loadingFallback={<PropertyFormRouteLoadingFallback />}
                >
                  <CondominiumPlaceholderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/duplicar-condominio/:id"
              element={
                <ProtectedRoute
                  permission="createProperties"
                  loadingFallback={<PropertyFormRouteLoadingFallback />}
                >
                  <DuplicateCondominium />
                </ProtectedRoute>
              }
            />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/contato" element={<Contact />} />
            <Route path="/sobre" element={<About />} />
            <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
            <Route path="/termos-de-uso" element={<TermsOfUse />} />
            <Route path="/favoritos" element={<Favorites />} />
            <Route path="/lista-compartilhada/:listId" element={<SharedList />} />
            <Route path="/comparacao" element={<Comparison />} />
            <Route path="/lp/preview" element={<LandingPreviewPage />} />
            <Route path="/lp/:slug" element={<LandingPublicPage />} />
            <Route path="/linkBio/:slug" element={<LinkBioPublicLayout />}>
              <Route index element={<LinkBioPublicPage />} />
              <Route path="imoveis" element={<LinkBioPropertiesPublicPage />} />
            </Route>
            <Route path="/linkbio/:slug" element={<LinkBioPublicLayout />}>
              <Route index element={<LinkBioPublicPage />} />
              <Route path="imoveis" element={<LinkBioPropertiesPublicPage />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </MainLayout>
      <Suspense fallback={null}>
        {location.pathname !== '/master' &&
          location.pathname !== '/login' &&
          !location.pathname.startsWith('/lp/') &&
          !isLinkBioPublicPathname(location.pathname) && <ChatButton />}
        {user && showAdminAiFabOutsideAdmin && <AdminAIFloatingButton />}
        {user && location.pathname !== '/login' && <TaskNotificationService />}
      </Suspense>
      {location.pathname.startsWith('/lp/') && <ScrollToTop variant="landing" />}
      <CookieConsentBanner />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ModalProvider>
          <Router>
            <ComparisonProvider>
              <CookieConsentProvider>
                <AppContent />
              </CookieConsentProvider>
            </ComparisonProvider>
          </Router>
        </ModalProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
